import React, { useState, useMemo, useEffect } from "react";
import { Modal } from "antd";
import styled from "styled-components";
import { HistoryOutlined, RightOutlined } from "@ant-design/icons";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import dayjs from "dayjs";
import KPIHistory from "../KPIHistory";

const FloatingBtn = styled.div`
  position: fixed;
  bottom: 24px;
  left: ${({ $left }) => $left || "304px"};
  z-index: 999;

  .floating-trigger {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #e8f5e9;
    color: #2e7d32;
    border: 1px solid #c8e6c9;
    border-radius: 20px;
    padding: 8px 14px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    transition: box-shadow 0.2s ease, transform 0.2s ease;
    user-select: none;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      transform: translateY(-1px);
    }

    .arrow-icon {
      font-size: 14px;
      line-height: 1;
    }
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-title {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a2e;
  }

  .ant-modal-body {
    padding: 16px 0 0 0;
  }
`;

const FloatingHistoryButton = ({ historyList = [], currentUserEmail = "" }) => {
  console.log("historyList-->", historyList);
  const [open, setOpen] = useState(false);

  // Re-read localStorage on every render + listen for sidebar toggle
  const [leftPosition, setLeftPosition] = useState(() => {
    const isCollapsed = window.localStorage.getItem("collapsed") === "true";
    return isCollapsed ? "100px" : "304px";
  });

  useEffect(() => {
    const update = () => {
      const isCollapsed = window.localStorage.getItem("collapsed") === "true";
      setLeftPosition(isCollapsed ? "100px" : "304px");
    };
    const interval = setInterval(update, 300);
    window.addEventListener("storage", update);
    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", update);
    };
  }, []);

  // Derive the button label and date from the last historyid record
  // by detecting which action field is filled
  const { actionLabel, actionDate } = useMemo(() => {
    if (!historyList?.length) return { actionLabel: null, actionDate: null };

    const sorted = [...historyList].sort((a, b) => a.historyid - b.historyid);

    // Field priority — check last record first (highest historyid)
    const actionFields = [
      {
        byKey: "reviewededby", // ← was kpireviewedby
        atKey: "reviewedat", // ← was kpireviewedat
        label: "Appraiser Reviewed",
      },
      {
        byKey: "reviewraftedby", // ← new field
        atKey: "reviewdraftedat", // ← new field
        label: "Review Drafted",
      },
      {
        byKey: "attendedby", // ← was kpiattendedby
        atKey: "attendedat", // ← was kpiattendedat
        label: "Appraisee Attended",
      },
      {
        byKey: "kpiupdatedby",
        atKey: "kpiupdatedat",
        label: "KPI Updated",
      },
      {
        byKey: "kpicreatedby",
        atKey: "kpicreatedat",
        label: "KPI Issued",
      },
      {
        byKey: "templatedraftedby",
        atKey: "templatedraftedat",
        label: "Template Drafted",
      },
      {
        byKey: "templatecreatedby",
        atKey: "templatecreatedat",
        label: "Template Created",
      },
    ];

    // Walk from last to first record, pick the most recent action
    for (const record of [...sorted].reverse()) {
      for (const { byKey, atKey, label } of actionFields) {
        const by = record[byKey];
        const at = record[atKey];
        const hasBy = by && by.trim() !== "";
        const hasAt = at && at.trim() !== "";
        if (hasBy || hasAt) {
          return {
            actionLabel: label,
            actionDate: hasAt ? dayjs(at).format("Do MMM YYYY") : null,
          };
        }
      }
    }

    return { actionLabel: null, actionDate: null };
  }, [historyList]);

  return (
    <>
      <FloatingBtn $left={leftPosition}>
        <div className="floating-trigger" onClick={() => setOpen(true)}>
          {actionLabel ? (
            <>
              <span>
                {actionLabel} on: <strong>{actionDate || "—"}</strong>
              </span>
              <ArrowOutwardIcon />
            </>
          ) : (
            <>
              <HistoryOutlined />
              <span>History</span>
            </>
          )}
        </div>
      </FloatingBtn>

      <StyledModal
        title="History"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={560}
        centered
      >
        <KPIHistory
          historyList={historyList}
          currentUserEmail={currentUserEmail}
        />
      </StyledModal>
    </>
  );
};

export default FloatingHistoryButton;
